"use client";

import { useState } from "react";
import {
  EllipsisVertical,
  Settings,
  MessageCircleQuestion,
  LogOut,
  BookOpen,
  Rocket,
  ArrowUpRight,
  GraduationCap,
  Scroll,
} from "lucide-react";
import { useSpotUser } from "@/utils/spotUser";
import { usePathname, useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { clearCache } from "@/utils/queryCache";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Switch } from "@/components/shadcn/switch";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/shadcn/avatar";
import { Badge } from "@/components/shadcn/badge";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/shadcn/sidebar";
import { openPylonChat } from "@/utils/pylon";
import {
  TOOLBAR_HIDDEN_KEY,
  TOOLBAR_VISIBILITY_EVENT,
} from "@/components/DeploymentToolbar/DeploymentToolbar";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { cn } from "@/lib/utils";
import { useBlockSidenavCollapse } from "./sidenavCollapseBlock";

const HELP_LINKS = [
  {
    title: "Documentation",
    href: "https://docs.noxus.ai",
    icon: BookOpen,
  },
  {
    title: "Noxus University",
    href: "https://www.noxus.ai/university",
    icon: GraduationCap,
  },
];

// Reuses the same query key as the changelog page so React Query dedupes
// the network request between the two consumers.
const fetchPublicChangelog = async (): Promise<{ title?: string }[]> => {
  const res = await fetch("/api/public/changelog");
  if (!res.ok) throw new Error(`Changelog fetch failed: ${res.status}`);
  return res.json();
};

// Strip everything after the version: `v1.2.7-alpha9 — name` → `v1.2.7`.
const VERSION_PREFIX_RE = /^v\d+(?:\.\d+){0,2}/;

const useLatestChangelogVersion = (): string | null => {
  const { data } = useQuery({
    queryKey: ["CHANGELOG_PUBLIC"],
    queryFn: fetchPublicChangelog,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  if (!data || data.length === 0) return null;
  const match = (data[0]?.title ?? "").trim().match(VERSION_PREFIX_RE);
  return match ? match[0] : null;
};

// The user at the foot of every sidenav, opening the account menu.
export const SideNavAccountMenu = () => {
  const setIsCollapseBlocked = useBlockSidenavCollapse("account");
  const { spotUser } = useSpotUser();
  const router = useRouter();
  const pathname = usePathname();
  const { isMobile } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);
  const latestVersion = useLatestChangelogVersion();

  const isOnSettings = pathname?.startsWith("/settings") ?? false;

  const userImage = spotUser.image;
  const userName = spotUser.display_name;
  const userInitials = userName
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <SidebarMenuItem>
      <DropdownMenu
        modal={false}
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          setIsCollapseBlocked(open);
        }}
      >
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            data-testid="sidebar-account-menu"
            className={cn(
              "h-8 p-1 gap-2 group-data-[collapsible=icon]:p-1! hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
              isOpen &&
                "bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent"
            )}
          >
            <Avatar className="size-6 rounded-[4px] shrink-0">
              {userImage && <AvatarImage src={userImage} alt={userName} />}
              <AvatarFallback className="rounded-[4px] bg-primary text-[0.6rem] font-medium tracking-wide text-primary-foreground">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight transition-all duration-200">
              <span className="truncate font-medium text-sidebar-foreground">
                {userName}
              </span>
            </div>
            <EllipsisVertical className="ml-auto size-4 text-sidebar-foreground/50 transition-all duration-200" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-w-xs rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-[4px] shrink-0">
                {userImage && <AvatarImage src={userImage} alt={userName} />}
                <AvatarFallback className="rounded-[4px] bg-primary text-sm font-medium text-primary-foreground">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 min-w-0 text-left text-sm leading-tight">
                <span className="truncate font-medium">{userName}</span>
                <span className="truncate text-xs">{spotUser.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          {(!isOnSettings || spotUser.is_admin) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {!isOnSettings && (
                  <DropdownMenuItem
                    onClick={() => router.push("/settings/profile")}
                  >
                    <Settings className="size-4" />
                    Settings
                  </DropdownMenuItem>
                )}
                {spotUser.is_admin && (
                  <DropdownMenuItem
                    onClick={() => {
                      const isHidden =
                        localStorage.getItem(TOOLBAR_HIDDEN_KEY) === "true";
                      localStorage.setItem(
                        TOOLBAR_HIDDEN_KEY,
                        isHidden ? "false" : "true"
                      );
                      window.dispatchEvent(new Event(TOOLBAR_VISIBILITY_EVENT));
                    }}
                  >
                    <Rocket className="size-4" />
                    Deployment toolbar
                    <Switch
                      checked={
                        localStorage.getItem(TOOLBAR_HIDDEN_KEY) !== "true"
                      }
                      className="ml-auto pointer-events-none"
                    />
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {HELP_LINKS.map(({ title, href, icon: Icon }) => (
              <DropdownMenuItem key={title} asChild>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="resource-link"
                  data-resource={title}
                  onClick={() =>
                    analytics_track(ANALYTICS_EVENTS.RESOURCES_CLICK, {
                      resource_name: title,
                    })
                  }
                >
                  <Icon className="size-4" />
                  {title}
                  <ArrowUpRight className="ml-auto size-4 text-muted-foreground" />
                </a>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => router.push("/changelog")}>
              <Scroll className="size-4" />
              Changelog
              {latestVersion && (
                <Badge
                  variant="outline"
                  isCompact
                  className="ml-auto font-normal text-muted-foreground tabular-nums"
                >
                  {latestVersion}
                </Badge>
              )}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="resource-link"
            data-resource="Contact support"
            onClick={() => {
              analytics_track(ANALYTICS_EVENTS.RESOURCES_CLICK, {
                resource_name: "Contact support",
              });
              openPylonChat();
            }}
          >
            <MessageCircleQuestion className="size-4" />
            Contact support
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="sidebar-logout"
            onClick={() => {
              clearCache();
              window.location.href = "/auth/logout";
            }}
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="size-4 text-destructive" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
